import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../utils/firebase";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { ChevronDown, HelpCircle, X, Plus } from "lucide-react";

const filterOptions = createFilterOptions();

/**
 * SchemaFormRenderer - Renders a dynamic form based on a schema definition.
 * 
 * @param {Object} props
 * @param {Object} props.schema - The schema definition with groups and fields
 * @param {Object} props.data - Current form data (controlled)
 * @param {Function} props.onChange - Called with (fieldKey, value) when data changes
 * @param {Object} props.errors - Object mapping field keys to error messages
 * @param {string[]} props.excludeFields - Field keys to exclude from rendering
 * @param {boolean} props.disabled - Whether all form fields should be disabled
 */
const SchemaFormRenderer = ({ schema, data = {}, onChange, errors = {}, excludeFields = [], disabled = false }) => {
  const [firestoreOptions, setFirestoreOptions] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  // Initialize expanded state from schema
  useEffect(() => {
    if (schema?.groups) {
      const initial = {};
      schema.groups.forEach(group => {
        initial[group.key] = group.defaultOpen !== false;
      });
      setExpandedGroups(initial);
    }
  }, [schema]);

  // Load Firestore options for firestoreSelect fields
  useEffect(() => {
    if (!schema?.fields) return;

    const firestoreFields = schema.fields.filter(
      f => f.type === "firestoreSelect" || f.type === "firestoreMultiSelect"
    );

    firestoreFields.forEach(async (field) => {
      try {
        let q = collection(db, field.collection);
        
        // Apply filter if specified
        if (field.filter) {
          q = query(q, where(field.filter.field, "==", field.filter.equals));
        }
        
        const snapshot = await getDocs(q);
        const options = snapshot.docs.map(doc => ({
          value: field.valueField ? doc.data()[field.valueField] : doc.id,
          label: doc.data()[field.displayField] || doc.id
        }));
        
        setFirestoreOptions(prev => ({
          ...prev,
          [field.key]: options
        }));
      } catch (error) {
        console.error(`Error loading options for ${field.key}:`, error);
      }
    });
  }, [schema]);

  // Check if a field/group should be visible based on showWhen condition
  const isVisible = (showWhen) => {
    if (!showWhen) return true;
    
    const fieldValue = getNestedValue(data, showWhen.field);
    
    if ("equals" in showWhen) {
      return fieldValue === showWhen.equals;
    }
    if ("notEquals" in showWhen) {
      return fieldValue !== showWhen.notEquals;
    }
    if ("in" in showWhen) {
      return showWhen.in.includes(fieldValue);
    }
    
    return true;
  };

  // Get nested value from data (e.g., "message.title" -> data.message.title)
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  // Set nested value in data
  const setNestedValue = (key, value) => {
    onChange(key, value);
  };

  // Get value with default fallback
  const getValue = (field) => {
    const val = getNestedValue(data, field.key);
    if (val !== undefined) return val;
    return field.defaultValue !== undefined ? field.defaultValue : "";
  };

  // Render a single field based on its type
  const renderField = (field) => {
    if (!isVisible(field.showWhen)) return null;

    const value = getValue(field);
    const error = errors[field.key];
    const helperText = error || field.helpText;
    // Base props for all form controls
    const baseProps = {
      fullWidth: true,
      size: "small",
      error: !!error,
      disabled: disabled,
    };
    // TextField accepts helperText directly, FormControl does not
    const commonProps = {
      ...baseProps,
      helperText,
    };

    switch (field.type) {
      case "text":
        return (
          <TextField
            {...commonProps}
            label={field.label}
            value={value || ""}
            onChange={(e) => setNestedValue(field.key, e.target.value)}
            required={field.required}
          />
        );

      case "textarea":
        return (
          <TextField
            {...commonProps}
            label={field.label}
            value={value || ""}
            onChange={(e) => setNestedValue(field.key, e.target.value)}
            multiline
            rows={4}
            required={field.required}
          />
        );

      case "number":
        return (
          <TextField
            {...commonProps}
            label={field.label}
            type="number"
            value={value ?? ""}
            onChange={(e) => setNestedValue(field.key, e.target.value ? Number(e.target.value) : null)}
            required={field.required}
            inputProps={{ min: field.min, max: field.max, step: field.step || 1 }}
          />
        );

      case "checkbox":
      case "boolean":
        return (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!value}
                  onChange={(e) => setNestedValue(field.key, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={field.label}
            />
            {field.helpText && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                {field.helpText}
              </Typography>
            )}
          </Box>
        );

      case "select":
        const sortedOptions = [...(field.options || [])].sort((a, b) => 
          (a.label || '').localeCompare(b.label || '')
        );
        return (
          <FormControl {...baseProps}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value ?? ""}
              label={field.label}
              onChange={(e) => {
                console.log('[SchemaFormRenderer] Select change:', field.key, e.target.value);
                setNestedValue(field.key, e.target.value);
              }}
              required={field.required}
              disabled={disabled}
              MenuProps={{ sx: { zIndex: 'var(--z-modal-components)' } }}
            >
              {sortedOptions.map(opt => (
                <MenuItem key={opt.value ?? "null"} value={opt.value ?? ""}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        );

      case "multiSelect":
        const sortedMultiOptions = [...(field.options || [])].sort((a, b) => 
          (a.label || '').localeCompare(b.label || '')
        );
        return (
          <FormControl {...baseProps}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              multiple
              value={Array.isArray(value) ? value : []}
              label={field.label}
              onChange={(e) => setNestedValue(field.key, e.target.value)}
              disabled={disabled}
              MenuProps={{ sx: { zIndex: 'var(--z-modal-components)' } }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(val => {
                    const opt = field.options?.find(o => o.value === val);
                    return <Chip key={val} label={opt?.label || val} size="small" />;
                  })}
                </Box>
              )}
            >
              {sortedMultiOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        );

      case "firestoreSelect":
        const fsOptions = [...(firestoreOptions[field.key] || [])].sort((a, b) => 
          (a.label || '').localeCompare(b.label || '')
        );
        
        // If allowCreate is enabled, use Autocomplete with freeSolo
        if (field.allowCreate) {
          // Find current option in loaded options, or create a placeholder if value exists but options not loaded
          let currentOption = fsOptions.find(opt => opt.value === value) || null;
          if (!currentOption && value) {
            // Value exists but not in loaded options - create a placeholder option
            currentOption = { value: value, label: value };
          }
          return (
            <Autocomplete
              freeSolo
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              disabled={disabled}
              options={fsOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.inputValue) return option.inputValue;
                return option.label || '';
              }}
              value={currentOption}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  // User typed and pressed enter - convert to snake_case
                  const snakeCase = newValue.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                  setNestedValue(field.key, snakeCase);
                } else if (newValue && newValue.inputValue) {
                  // User selected "Create: xxx" option - convert to snake_case
                  const snakeCase = newValue.inputValue.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                  setNestedValue(field.key, snakeCase);
                } else if (newValue) {
                  // User selected existing option
                  setNestedValue(field.key, newValue.value);
                } else {
                  setNestedValue(field.key, null);
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filterOptions(options, params);
                const { inputValue } = params;
                // Suggest creating a new value
                const isExisting = options.some((option) => inputValue.toLowerCase() === option.label.toLowerCase());
                if (inputValue !== '' && !isExisting) {
                  const snakeCase = inputValue.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                  filtered.push({
                    inputValue,
                    label: `Create "${inputValue}" (${snakeCase})`,
                    value: snakeCase
                  });
                }
                return filtered;
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return <li key={option.value || option.inputValue} {...rest}>{option.label}</li>;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={field.label}
                  size="small"
                  error={!!error}
                  helperText={error || field.helpText}
                  required={field.required}
                />
              )}
              componentsProps={{
                popper: { sx: { zIndex: 'var(--z-modal-components)' } }
              }}
              fullWidth
            />
          );
        }
        
        // Standard Select for non-creatable
        // Ensure current value is in options to prevent MUI warning
        const currentValueInOptions = fsOptions.some(opt => opt.value === value);
        const selectOptions = (value && !currentValueInOptions) 
          ? [...fsOptions, { value: value, label: value }]
          : fsOptions;
        
        return (
          <FormControl {...baseProps}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value || ""}
              label={field.label}
              onChange={(e) => setNestedValue(field.key, e.target.value || null)}
              required={field.required}
              disabled={disabled}
              MenuProps={{ sx: { zIndex: 'var(--z-modal-components)' } }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {selectOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        );

      case "firestoreMultiSelect":
        const fmsOptions = [...(firestoreOptions[field.key] || [])].sort((a, b) =>
          (a.label || '').localeCompare(b.label || '')
        );
        const selectedValues = Array.isArray(value) ? value : [];
        const selectedOptions = selectedValues
          .map(v => fmsOptions.find(opt => opt.value === v) || { value: v, label: v })
          ;
        return (
          <Autocomplete
            multiple
            disabled={disabled}
            options={fmsOptions}
            getOptionLabel={(option) => option.label || ''}
            value={selectedOptions}
            onChange={(event, newValue) => {
              setNestedValue(field.key, newValue.map(opt => opt.value));
            }}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.label}
                    size="small"
                    {...chipProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                size="small"
                error={!!error}
                helperText={error || field.helpText}
                required={field.required}
              />
            )}
            componentsProps={{
              popper: { sx: { zIndex: 'var(--z-modal-components)' } }
            }}
            fullWidth
          />
        );

      case "multiText":
        return (
          <MultiTextInput
            label={field.label}
            value={Array.isArray(value) ? value : []}
            onChange={(newVal) => setNestedValue(field.key, newVal)}
            helpText={field.helpText}
            error={error}
            disabled={disabled}
          />
        );

      default:
        return (
          <Typography color="error" variant="caption">
            Unknown field type: {field.type}
          </Typography>
        );
    }
  };

  // Group fields by their group key
  const getFieldsByGroup = (groupKey) => {
    return schema.fields?.filter(f => f.group === groupKey && !excludeFields.includes(f.key)) || [];
  };

  // Get ungrouped fields
  const getUngroupedFields = () => {
    const groupKeys = (schema.groups || []).map(g => g.key);
    return schema.fields?.filter(f => (!f.group || !groupKeys.includes(f.group)) && !excludeFields.includes(f.key)) || [];
  };

  if (!schema) {
    return (
      <Box sx={{ p: 2, color: 'text.secondary' }}>
        No schema loaded. Configure a schema in the Schema tab.
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Ungrouped fields first */}
      {getUngroupedFields().map(field => (
        <Box key={field.key}>
          {renderField(field)}
        </Box>
      ))}

      {/* Grouped fields in accordions */}
      {schema.groups?.map(group => {
        if (!isVisible(group.showWhen)) return null;
        
        const groupFields = getFieldsByGroup(group.key);
        const visibleFields = groupFields.filter(f => isVisible(f.showWhen));
        
        if (visibleFields.length === 0) return null;

        return (
          <Accordion
            key={group.key}
            expanded={expandedGroups[group.key] || false}
            onChange={(e, expanded) => setExpandedGroups(prev => ({ ...prev, [group.key]: expanded }))}
            sx={{ 
              boxShadow: 'none', 
              border: '1px solid var(--color-border)',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <Box>
                <Typography sx={{ fontWeight: 500 }}>{group.label}</Typography>
                {group.description && (
                  <Typography variant="caption" color="textSecondary">
                    {group.description}
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {groupFields.map(field => (
                  <Box key={field.key}>
                    {renderField(field)}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

/**
 * MultiTextInput - Tag-style input for arrays of strings
 */
const MultiTextInput = ({ label, value = [], onChange, helpText, error, disabled = false }) => {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue("");
    }
  };

  const handleRemove = (item) => {
    if (disabled) return;
    onChange(value.filter(v => v !== item));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ 
        border: '1px solid', 
        borderColor: error ? 'error.main' : 'var(--color-border)',
        borderRadius: 1, 
        p: 1,
        minHeight: 42,
        opacity: disabled ? 0.6 : 1
      }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: value.length > 0 ? 1 : 0 }}>
          {value.map(item => (
            <Chip
              key={item}
              label={item}
              size="small"
              onDelete={disabled ? undefined : () => handleRemove(item)}
              deleteIcon={disabled ? undefined : <X size={14} />}
            />
          ))}
        </Box>
        {!disabled && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type and press Enter"
            variant="standard"
            sx={{ flex: 1 }}
            InputProps={{ disableUnderline: true }}
          />
          <Tooltip title="Add">
            <IconButton size="small" onClick={handleAdd} disabled={!inputValue.trim()}>
              <Plus size={16} />
            </IconButton>
          </Tooltip>
        </Box>
        )}
      </Box>
      {(error || helpText) && (
        <Typography variant="caption" color={error ? "error" : "textSecondary"} sx={{ mt: 0.5, display: 'block' }}>
          {error || helpText}
        </Typography>
      )}
    </Box>
  );
};

export default SchemaFormRenderer;
